import React, { useState, useContext } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import { Headline } from "../../components/Headline";
import FormField from "../../components/form-components/FormField";
import { FormSent } from "../FormSent/FormSent";
import FormLayoutContext from "../../layouts/StandardFormLayout/context";
import GeneralPageContext from "../../templates/GeneralPageContext";

import t from "../../lib/translate";
import FormService from "../../lib/service/formService";

import FocusError from "../FocusError";
import { useThermoBlock, useSensorBlock } from "./components";
import { useThermoConfig, useSensorConfig } from "./config";

function RegistrationForm({ formId, locale, locationState = {}, isSending, sendable }) {
    const [isSent, setIsSent] = useState(false);
    const [formSelected, setFormSelected] = useState("");
    const { setShowSubmit } = useContext(FormLayoutContext);
    const { cameras } = useContext(GeneralPageContext);

    // unfortunatly translate uses a hook so we have to preload a bunch of stuff
    const ThermoBlock = useThermoBlock(cameras);
    const SensorBlock = useSensorBlock();
    const { initialThermoValues, thermoSchema } = useThermoConfig(t, cameras);
    const { initialSensorValues, sensorSchema } = useSensorConfig(t);
    const formSchema = {
        thermo: thermoSchema,
        sensor: sensorSchema,
    };
    const thermoSubheadline = t("form.registration.thermo.subheadline");

    const service = new FormService(
        process.env.GATSBY_FORM_MICROSERVICE_BASE_URL,
        locale
    );

    const submit = (userData) => {
        const formData = {
            ...userData,
            language: locale,
            url: locationState?.prevUrl ?? "",
            tGdpr: userData.tGdpr ? "1" : "0",
            sGdpr: userData.sGdpr ? "1" : "0",
            email: userData.sEmail || userData.tEmail, // form service limitations
            ...(formSelected === 'thermo'
                ? {
                    manual: userData.manual ? "1" : "0",
                    download: userData.download ? "1" : "0",
                    camera: userData.camera ? "1" : "0",
                    certificate: userData.certificate ? "1" : "0",
                    calibration: userData.calibration ? "1" : "0",
                    misc: userData.misc ? "1" : "0",
                }
                : {}
            )
        };

        isSending(true);
        service
            .submit(`${formSelected}-${formId}`, formData)
            .then((res) => {
                if (res === null) {
                    window?.dataLayer?.push({
                        event: "form_successfully_submitted"
                    });
                    setIsSent(true);
                    setShowSubmit(false);
                    isSending(false);
                }
            })
            .catch((error) => {
                console.error(error);
                isSending(false);
                sendable(true);
            });
    };

    const fieldSelection = formik => e => {
        if (e.target.value === '0') {
            setFormSelected('');
            sendable(false);
        } else if (e.target.value === '1') {
            setFormSelected('thermo');
            sendable(true);
        } else if (e.target.value === '2') {
            setFormSelected('sensor');
            sendable(true);
        }

        formik.handleChange(e);
    }

    return <>
        {!isSent && (
            <Formik
                initialValues={{
                    field: "",
                    ...initialThermoValues,
                    ...initialSensorValues,
                }}
                validationSchema={Yup.object({
                    field: Yup.string()
                        .oneOf(['0','1','2']),
                    ...(formSchema[formSelected] || {})
                })}
                onSubmit={submit}
            >
                {(formik) => 
                    <Form id={formId}>
                        <legend>
                            <Headline semanticOfHeadline="H1">
                                {t("form.registration.headline")}
                            </Headline>
                            <p>{t("form.registration.subheadline")}</p>
                        </legend>
                        <FormField
                            type="select"
                            name="field"
                            label={t("form.registration.field.label")}
                            options={[
                                {value: 0, label: t("form.registration.field.value-00")},
                                {value: 1, label: t("form.registration.field.value-01")},
                                {value: 2, label: t("form.registration.field.value-02")}
                            ]}
                            onChange={fieldSelection(formik)}
                            required
                        />
                        {formSelected === 'thermo' && <>
                            <legend>
                                <Headline semanticOfHeadline="H2">
                                    {thermoSubheadline}
                                </Headline>
                            </legend>
                            {ThermoBlock}
                        </>}
                        {formSelected === 'sensor' && SensorBlock}
                        <FocusError />
                    </Form>
                }
            </Formik>
        )} {isSent && <FormSent />}
    </>;
}

export { RegistrationForm };
